import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TablePagination,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';

interface CreditUsage {
  id: string;
  safety_check_id: string | null;
  staff_member_id: string | null;
  is_staff_check: boolean;
  driving_license_number: string;
  check_status: string;
  driver_name: string | null;
  license_status: string | null;
  points_total: number;
  created_at: string;
  safety_check_type: string | null;
  staff_member_name: string | null;
  user_name: string | null;
  check_source: string;
}

interface PendingToken {
  token_id: string;
  safety_check_id: string;
  borrower_email: string;
  expires_at: string;
  created_at: string;
  check_name: string | null;
  check_type: string | null;
  asset_name: string | null;
  created_by_first_name: string | null;
  created_by_last_name: string | null;
}

interface PendingRequest {
  id: string;
  recipient_email: string;
  recipient_first_name: string | null;
  recipient_last_name: string | null;
  status: string;
  expires_at: string;
  created_at: string;
  requester_first_name: string | null;
  requester_last_name: string | null;
}

interface DVLACreditData {
  credits: {
    current: number;
    pending: number;
    available: number;
    pendingTokens: number;
    pendingRequests: number;
  };
  stats: {
    totalChecks: number;
    completedChecks: number;
    failedChecks: number;
    skippedChecks: number;
    creditsUsed: number;
    checksThisMonth: number;
    checksThisWeek: number;
    staffChecks: number;
    loanChecks: number;
  };
  usageHistory: CreditUsage[];
  pendingTokens: PendingToken[];
  pendingRequests: PendingRequest[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

const DVLACreditUsage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DVLACreditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');
  const [hasBusinessId, setHasBusinessId] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Get current user's business ID
      const userResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const role = userResponse.data.role;
      const businessId = userResponse.data.businessId;
      const isMultiBusinessManager = role === 'Multi Business Manager' || userResponse.data.isMultiBusinessManager === true;
      const isSuperAdmin = role === 'Super Admin';
      const isOwner = role === 'Owner';
      
      // Update component state
      setUserRole(role);
      setHasBusinessId(!!businessId);
      
      const params: any = {
        page: page + 1,
        limit: rowsPerPage
      };
      
      // Multi Business Managers and Super Admins must have a businessId (must be in business context)
      if (isMultiBusinessManager || isSuperAdmin) {
        if (!businessId) {
          throw new Error('Please switch to a business context to view DVLA credit data.');
        }
        params.business_id = businessId;
      }
      // Regular users - add business_id if they have one
      else if (businessId) {
        params.business_id = businessId;
      } else if (!isOwner) {
        // Regular user without business ID - this is an error
        throw new Error('User business ID not found. Please contact support.');
      }
      
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      
      if (dateTo) {
        params.date_to = dateTo;
      }

      // Use the existing owner endpoint - it works for all authenticated users
      const response = await axios.get(`${API_BASE_URL}/owner/dvla-audit-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      // Transform the response to match our expected format
      const transformedData = {
        credits: {
          current: 0, // Will fetch separately
          pending: 0,
          available: 0,
          pendingTokens: 0,
          pendingRequests: 0
        },
        stats: {
          totalChecks: response.data.stats.totalChecks,
          completedChecks: response.data.stats.completedChecks,
          failedChecks: response.data.stats.failedChecks,
          skippedChecks: 0,
          creditsUsed: response.data.stats.totalCreditsUsed,
          checksThisMonth: response.data.stats.checksThisMonth,
          checksThisWeek: 0,
          staffChecks: response.data.checks.filter((c: any) => c.check_source === 'Staff Management').length,
          loanChecks: response.data.checks.filter((c: any) => c.check_source === 'Loan Check').length
        },
        usageHistory: response.data.checks,
        pendingTokens: [],
        pendingRequests: [],
        pagination: {
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.stats.totalChecks,
          totalPages: Math.ceil(response.data.stats.totalChecks / rowsPerPage),
          hasMore: response.data.pagination.hasMore
        }
      };

      // Fetch current credits (only if business_id is present)
      if (params.business_id) {
        try {
          const businessResponse = await axios.get(`${API_BASE_URL}/businesses/current`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          transformedData.credits.current = businessResponse.data.dvla_credits || 0;
        } catch (err) {
          console.error('Error fetching current credits:', err);
        }

        // Fetch pending items (only for business users)
        try {
          const [tokensResponse, requestsResponse] = await Promise.all([
            axios.get(`${API_BASE_URL}/dvla-credits/pending-tokens`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: [] })),
            axios.get(`${API_BASE_URL}/dvla-license-requests?status=pending`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: [] }))
          ]);
          
          transformedData.pendingTokens = tokensResponse.data || [];
          transformedData.pendingRequests = requestsResponse.data || [];
          transformedData.credits.pendingTokens = transformedData.pendingTokens.length;
          transformedData.credits.pendingRequests = transformedData.pendingRequests.length;
          transformedData.credits.pending = transformedData.credits.pendingTokens + transformedData.credits.pendingRequests;
          transformedData.credits.available = Math.max(0, transformedData.credits.current - transformedData.credits.pending);
        } catch (err) {
          console.error('Error fetching pending items:', err);
        }
      } else {
        // For super admin without business, show message instead
        console.log('Super Admin view - no specific business selected');
      }

      setData(transformedData);
    } catch (err: any) {
      console.error('Error fetching DVLA credit data:', err);
      setError(err.response?.data?.message || 'Failed to fetch DVLA credit data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'skipped':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'failed':
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'skipped':
        return <ScheduleIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            DVLA Credit Usage
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" onClick={() => navigate('/admin/dvla')}>
              Back to DVLA Management
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '800px' }}>
          View all DVLA credit usage, including completed checks, pending requests, and available credits.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!hasBusinessId && userRole === 'Owner' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          As an Owner, you can view all DVLA checks across all businesses. Credit summaries are not available in this view.
        </Alert>
      )}

      {/* Credit Summary Cards */}
      {data && hasBusinessId && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Available Credits Card */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Current Credits
                </Typography>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {data.credits.current}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total credits
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Pending Credits Card */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Reserved Credits
                </Typography>
                <Typography variant="h3" color="warning.main" fontWeight="bold">
                  {data.credits.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {data.credits.pendingTokens} tokens, {data.credits.pendingRequests} requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Available Credits Card */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Available Credits
                </Typography>
                <Typography variant="h3" color="success.main" fontWeight="bold">
                  {data.credits.available}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Ready to use
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Credits Used Card */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Credits Used
                </Typography>
                <Typography variant="h3" color="info.main" fontWeight="bold">
                  {data.stats.creditsUsed}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  All-time usage
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Stats Cards */}
      {data && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Check Statistics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Checks
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {data.stats.totalChecks}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="h5" color="success.main">
                      {data.stats.completedChecks}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      This Week
                    </Typography>
                    <Typography variant="h5" color="info.main">
                      {data.stats.checksThisWeek}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      This Month
                    </Typography>
                    <Typography variant="h5" color="info.main">
                      {data.stats.checksThisMonth}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Check Sources
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Staff Management
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {data.stats.staffChecks}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Loan Checks
                    </Typography>
                    <Typography variant="h5" color="secondary">
                      {data.stats.loanChecks}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Failed
                    </Typography>
                    <Typography variant="h5" color="error.main">
                      {data.stats.failedChecks}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Skipped
                    </Typography>
                    <Typography variant="h5" color="warning.main">
                      {data.stats.skippedChecks}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Pending Items */}
      {data && hasBusinessId && (data.pendingTokens.length > 0 || data.pendingRequests.length > 0) && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <PendingIcon color="warning" />
              <Typography variant="h6">
                Pending Items ({data.credits.pending})
              </Typography>
              <Chip 
                label={`${data.credits.pendingTokens} Tokens`} 
                size="small" 
                color="warning"
              />
              <Chip 
                label={`${data.credits.pendingRequests} Requests`} 
                size="small" 
                color="warning"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Pending Tokens */}
            {data.pendingTokens.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Pending License Submission Tokens (Loan Checks)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These are loan checks where the borrower hasn't submitted their license yet.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Check Name</TableCell>
                        <TableCell>Asset</TableCell>
                        <TableCell>Borrower Email</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell>Created By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.pendingTokens.map((token) => (
                        <TableRow key={token.token_id}>
                          <TableCell>{token.check_name || 'N/A'}</TableCell>
                          <TableCell>{token.asset_name || 'N/A'}</TableCell>
                          <TableCell>{token.borrower_email}</TableCell>
                          <TableCell>{formatDate(token.created_at)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={formatDate(token.expires_at)} 
                              size="small"
                              color="warning"
                            />
                          </TableCell>
                          <TableCell>
                            {token.created_by_first_name && token.created_by_last_name
                              ? `${token.created_by_first_name} ${token.created_by_last_name}`
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {data.pendingTokens.length > 0 && data.pendingRequests.length > 0 && <Divider sx={{ my: 3 }} />}

            {/* Pending Requests */}
            {data.pendingRequests.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Pending License Check Requests (Staff Management)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These are staff license check requests that haven't been completed yet.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Recipient Name</TableCell>
                        <TableCell>Recipient Email</TableCell>
                        <TableCell>Requested By</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Expires</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.pendingRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            {request.recipient_first_name && request.recipient_last_name
                              ? `${request.recipient_first_name} ${request.recipient_last_name}`
                              : request.recipient_first_name || request.recipient_last_name || 'N/A'}
                          </TableCell>
                          <TableCell>{request.recipient_email}</TableCell>
                          <TableCell>
                            {request.requester_first_name && request.requester_last_name
                              ? `${request.requester_first_name} ${request.requester_last_name}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{formatDate(request.created_at)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={formatDate(request.expires_at)} 
                              size="small"
                              color="warning"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="skipped">Skipped</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(0);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(0);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={clearFilters}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Usage History Table */}
      {data && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Credit Usage History
            </Typography>
            {loading && <CircularProgress size={24} />}
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>License Number</TableCell>
                  <TableCell>Driver Name</TableCell>
                  <TableCell>License Status</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Check Name</TableCell>
                  <TableCell>Performed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.usageHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No DVLA checks found. DVLA checks from loan checks, staff management, and safety checks will appear here once performed.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.usageHistory.map((usage) => (
                    <TableRow key={usage.id} hover>
                      <TableCell>{formatDate(usage.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(usage.check_status)}
                          label={usage.check_status}
                          color={getStatusColor(usage.check_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={usage.check_source}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {usage.driving_license_number}
                      </TableCell>
                      <TableCell>{usage.driver_name || 'N/A'}</TableCell>
                      <TableCell>
                        {usage.license_status ? (
                          <Chip label={usage.license_status} size="small" color="default" />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={usage.points_total}
                          size="small"
                          color={usage.points_total > 0 ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {usage.check_source === 'Staff Management' ? 'Staff Check' : usage.safety_check_type || usage.check_source}
                      </TableCell>
                      <TableCell>
                        {usage.staff_member_name || usage.user_name || 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {data.pagination && data.usageHistory.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={data.pagination.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      )}
    </Container>
  );
};

export default DVLACreditUsage;

